/**
 * Shown to super admins and admins while site maintenance mode is on.
 * Makes it obvious the public site is not fully visible to other users.
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { MaintenanceTrafficConeIcon } from '@components/features/maintenance';
import { useAuth } from '@contexts/AuthContext';
import { MAINTENANCE_BANNER_HEIGHT_CSS_VAR } from '@utils/maintenanceBannerLayout';
import { ADMIN_MAINTENANCE_BANNER_MESSAGE } from '@utils/siteMaintenanceMode';
import { isSuperAdminRole } from '@utils/roles';

import './MaintenanceModeAdminBanner.css';

function syncMaintenanceBannerHeight(banner: HTMLElement): void {
  const heightPx = `${banner.getBoundingClientRect().height}px`;
  document.documentElement.style.setProperty(MAINTENANCE_BANNER_HEIGHT_CSS_VAR, heightPx);
}

export function MaintenanceModeAdminBanner() {
  const { user } = useAuth();
  const isSuperAdmin = isSuperAdminRole(user?.role);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;

    syncMaintenanceBannerHeight(banner);

    const observer = new ResizeObserver(() => {
      syncMaintenanceBannerHeight(banner);
    });
    observer.observe(banner);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(MAINTENANCE_BANNER_HEIGHT_CSS_VAR);
    };
  }, []);

  return (
    <div
      ref={bannerRef}
      className="bhg-maintenance-admin-banner"
      role="alert"
      aria-live="polite"
    >
      <div className="bhg-maintenance-admin-banner__inner">
        <span className="bhg-maintenance-admin-banner__icon" aria-hidden>
          <MaintenanceTrafficConeIcon size="lg" className="bhg-traffic-cone-icon--banner" />
        </span>
        <div className="bhg-maintenance-admin-banner__content">
          <p className="bhg-maintenance-admin-banner__title">Site maintenance is on</p>
          <p className="bhg-maintenance-admin-banner__message">
            {ADMIN_MAINTENANCE_BANNER_MESSAGE}
          </p>
        </div>
        {isSuperAdmin ? (
          <Link to="/settings/app" className="bhg-maintenance-admin-banner__action">
            App settings
          </Link>
        ) : (
          <span className="bhg-maintenance-admin-banner__hint">
            Contact a super admin to turn maintenance off.
          </span>
        )}
      </div>
    </div>
  );
}
